import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Container, Section } from 'components/base';
import {
  Accordion,
  CloudinaryPicture,
  HtmlContent,
  ScriptMarkupFaq,
  faqContentToItems,
  getCloudinaryUrl,
  getCloudinaryDprSrcSet,
} from 'components/rich';
import useDataContext from 'data/context/data';

import { Head, Layout } from '../../components/layout';
import {
  AppPromoSection,
  NewsletterSection,
  ProductsSection,
} from '../../components/custom/mainSections';
import HeroBanner from '../../components/custom/heroBanner';
import SETTINGS from '../../settings';
import tracker from '../../lib/tracker';

// App Store link with attribution per category campaign (see docs/posthog.md).
const appStoreUrl = campaign => {
  const url = new URL(SETTINGS.APP_STORE_URL);
  url.searchParams.set('utm_source', 'website');
  url.searchParams.set('utm_medium', 'category');
  url.searchParams.set('utm_campaign', campaign);
  return url.href;
};

const CategoryPage = props => {
  const dataContext = useDataContext();

  const category = dataContext.getCategory(props.categoryId);
  const { appPromo, faq } = props.content;

  const appPromoHref = useMemo(
    () => (appPromo ? appStoreUrl(appPromo.campaign) : null),
    [appPromo],
  );

  const handleAppPromoClick = () => {
    tracker.onCustomEvent(`${appPromo.campaign}_cta_click`, {
      cta: 'app_store',
      location: 'promo',
    });
  };

  return (
    <React.Fragment key={category.id}>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={props.content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={category.banner ? getCloudinaryUrl({ imageKey: category.image }) : null}
        // The full-width banner renders the plain DPR pair (the categories ladder tops out at
        // w960, too small for its box) — mirror that pair so the preload matches at DPR >= 2.
        heroImageSrcSet={category.banner ? getCloudinaryDprSrcSet({ imageKey: category.image }) : null}
      />

      {faq && <ScriptMarkupFaq items={faqContentToItems(faq)} />}

      <Layout>
        {category.banner && (
          <Section className="py-0">
            <HeroBanner
              title={(category.heroTitle ?? category.name).toUpperCase()}
              description={category.shortDescription}
            >
              <CloudinaryPicture imageKey={category.image} className="w-100" />
            </HeroBanner>
          </Section>
        )}

        {props.content.intro && (
          <Section className="pb-0">
            <Container>
              <HtmlContent className="text-editorial" html={props.content.intro} />
            </Container>
          </Section>
        )}

        {category.data.map(item => (
          <ProductsSection
            key={item.key}
            title={item.title}
            subCategories={item.subCategories}
            products={item.products}
            productsFirst={item.productsFirst}
            xs={12}
            md={4}
          />
        ))}

        {props.content.extraDescription && (
          <Section>
            <Container className="my-5">
              <HtmlContent className="text-editorial" html={props.content.extraDescription} />
            </Container>
          </Section>
        )}

        {appPromo && (
          <AppPromoSection
            eyebrow={appPromo.eyebrow}
            title={appPromo.title}
            body={appPromo.body}
            href={appPromoHref}
            onCtaClick={handleAppPromoClick}
          />
        )}

        {faq && (
          <Section>
            <Container>
              <Accordion title="FAQ">
                {faq.map(item => (
                  <Accordion.Item key={item.caption} title={item.caption}>
                    <HtmlContent html={item.description} />
                  </Accordion.Item>
                ))}
              </Accordion>
            </Container>
          </Section>
        )}

        <NewsletterSection />
      </Layout>
    </React.Fragment>
  );
};

CategoryPage.getInitialProps = async () => {
  throw new Error('Cannot access this page directly. See /[...params] page');
};

CategoryPage.propTypes = {
  categoryId: PropTypes.string.isRequired,
  content: PropTypes.object,
};

export default CategoryPage;
